import { useState } from "react";
import { AlertTriangle, Bell, BellRing, Info, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { IOSInstallSteps } from "../chalk/AddToHomeScreenCard";
import { BottomSheet } from "../chalk/primitives";
import { followSupport, followTeam, followedTeams, unfollowTeam } from "../../lib/tournamentFollow";
import { fmtSlot } from "./shared";

export const INK = "#1C2B1E";
export const CREAM = "#F5F0E8";
export const GREEN = "#2E7D52";

export function AnnouncementBanner({ items }) {
  if (!items.length) return null;
  return (
    <div data-testid="public-announcements" style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
      {items.map((a) => {
        const urgent = a.level === "urgent";
        const Icon = urgent ? AlertTriangle : Info;
        return (
          <div key={a.id} data-testid={`public-announcement-${a.id}`} role={urgent ? "alert" : "status"}
               style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 10,
                        background: urgent ? "#C0392B" : "rgba(245,240,232,0.08)", color: CREAM,
                        border: urgent ? "none" : "0.5px solid rgba(245,240,232,0.18)",
                        animation: "pt-fade 300ms ease both" }}>
            <Icon size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 14, lineHeight: 1.4, fontWeight: urgent ? 700 : 400 }}>{a.body}</div>
          </div>
        );
      })}
    </div>
  );
}

function liveState(g, now) {
  if (!g.scheduled_at || g.status !== "scheduled") return null;
  const s = new Date(g.scheduled_at).getTime();
  const e = s + (g.duration_min || 60) * 60000;
  if (now >= s && now < e) return "now";
  return now < s ? "upcoming" : "late";
}

export function LocationSchedule({ data, divisionId, teamsById }) {
  const tz = data.tournament.timezone;
  const now = Date.now();
  const games = data.games.filter((g) => !g.is_bye && g.court_id && g.scheduled_at
                                  && (divisionId === "all" || g.division_id === divisionId))
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  if (!data.courts.length || !games.length) {
    return <div data-testid="public-schedule-empty" style={{ opacity: 0.7, fontSize: 14 }}>The schedule hasn't been posted yet.</div>;
  }
  return (
    <div data-testid="public-location-schedule" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
      {data.courts.map((c) => {
        const mine = games.filter((g) => g.court_id === c.id);
        const current = mine.find((g) => liveState(g, now) === "now" || liveState(g, now) === "late");
        const upcoming = mine.filter((g) => liveState(g, now) === "upcoming" && g !== current);
        const done = mine.filter((g) => g.status !== "scheduled").slice(-1);
        return (
          <div key={c.id} data-testid={`public-location-${c.id}`} style={{ background: "rgba(245,240,232,0.06)", borderRadius: 12, padding: 12,
                                                                     border: "0.5px solid rgba(245,240,232,0.14)" }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: "0.04em" }}>{c.name}</div>
            {current && <Slot g={current} tag="NOW" teamsById={teamsById} tz={tz} hot />}
            {upcoming[0] && <Slot g={upcoming[0]} tag="UP NEXT" teamsById={teamsById} tz={tz} />}
            {upcoming.slice(1, 4).map((g) => <Slot key={g.id} g={g} tag="LATER" teamsById={teamsById} tz={tz} dim />)}
            {!current && !upcoming.length && done.map((g) => <Slot key={g.id} g={g} tag="LAST" teamsById={teamsById} tz={tz} dim />)}
            {!mine.length && <div style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>No games at this location.</div>}
          </div>
        );
      })}
    </div>
  );
}

function Slot({ g, tag, teamsById, tz, hot, dim }) {
  const name = (id) => (id ? teamsById[id]?.name || "TBD" : "TBD");
  return (
    <div data-testid={`public-slot-${g.id}`} style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, opacity: dim ? 0.7 : 1,
                                                      background: hot ? GREEN : "rgba(245,240,232,0.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800, letterSpacing: "0.1em" }}>
        <span>{tag}</span><span>{fmtSlot(g.scheduled_at, tz, false)}</span>
      </div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{name(g.home_team_id)} <span style={{ opacity: 0.6 }}>vs</span> {name(g.away_team_id)}</div>
      {g.status !== "scheduled" && <div style={{ fontSize: 12, opacity: 0.8 }}>Final {g.home_score ?? "-"}–{g.away_score ?? "-"}</div>}
    </div>
  );
}

export function FollowButton({ slug, teams, divisions }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(() => followedTeams(slug));
  const [busy, setBusy] = useState(null);
  const support = followSupport();
  const divName = Object.fromEntries(divisions.map((d) => [d.id, d.name]));

  async function toggle(team) {
    setBusy(team.id);
    try {
      setIds(ids.includes(team.id) ? await unfollowTeam(slug, team.id) : await followTeam(slug, team.id));
      toast.success(ids.includes(team.id) ? `Unfollowed ${team.name}.` : `Following ${team.name}. We'll alert you on scores and location/time changes.`);
    } catch (e) { toast.error(e.message || "Couldn't update alerts."); }
    finally { setBusy(null); }
  }
  return (
    <>
      <button data-testid="public-follow-open" onClick={() => setOpen(true)}
              style={{ display: "inline-flex", gap: 6, alignItems: "center", background: CREAM, color: INK, border: "none",
                       borderRadius: 999, padding: "9px 16px", fontWeight: 800, fontSize: 13, cursor: "pointer",
                       transition: "transform 150ms ease" }}>
        {ids.length ? <BellRing size={15} aria-hidden="true" /> : <Bell size={15} aria-hidden="true" />}
        {ids.length ? `Following ${ids.length}` : "Follow my team"}
      </button>
      <div style={{ color: INK }}>
      <BottomSheet isOpen={open} title="Follow my team" onDismiss={() => setOpen(false)}>
        <div data-testid="public-follow-sheet" style={{ display: "flex", flexDirection: "column", gap: 10, color: INK }}>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--chalk-muted)" }}>
            Get a push alert when your team's game goes final or its location/time changes, plus urgent announcements.
            No account needed. This page updates live for everyone either way.
          </div>
          {support === "ios_needs_home_screen" && (
            <div data-testid="public-follow-ios" style={{ background: "#FBF8F2", borderRadius: 10, padding: 12 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700, fontSize: 13 }}>
                <Smartphone size={15} aria-hidden="true" /> On iPhone, alerts need "Add to Home Screen"
              </div>
              <div style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                Apple only allows web alerts from apps on your Home Screen. Add this page, open it from the new icon, then tap Follow.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                <IOSInstallSteps />
              </div>
            </div>
          )}
          {support === "unsupported" && (
            <div data-testid="public-follow-unsupported" style={{ fontSize: 13 }}>
              This browser doesn't support alerts. Keep this page open — it refreshes every 15 seconds.
            </div>
          )}
          {support === "ok" && teams.map((tm) => (
            <button key={tm.id} data-testid={`public-follow-team-${tm.id}`} disabled={busy === tm.id} onClick={() => toggle(tm)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px",
                             borderRadius: 10, border: "0.5px solid var(--chalk-border)", cursor: "pointer",
                             background: ids.includes(tm.id) ? "#E3F1E8" : "#fff", color: INK, fontSize: 14 }}>
              <span>{tm.name}{divisions.length > 1 && <span style={{ color: "var(--chalk-muted)", fontSize: 12 }}> · {divName[tm.division_id]}</span>}</span>
              <span style={{ fontWeight: 700, color: GREEN, fontSize: 12 }}>{ids.includes(tm.id) ? "Following ✓" : "Follow"}</span>
            </button>
          ))}
        </div>
      </BottomSheet>
      </div>
    </>
  );
}
